import React, { useState, useEffect } from 'react';
import { Settings, Calendar, Clock, Users, Percent, Save, Plus, X, Loader2, AlertCircle, CalendarOff, CheckCircle2 } from 'lucide-react';
import { getRestaurantConfigs, updateRestaurantConfig } from '../services/apiClient';
import { API_BASE_URL } from '../constants/config';
import logger from '../utils/logger';
import { PageShell, Card, Button, Badge, Modal, cn } from '../components/ui';
import { controlClasses } from '../components/ui/Field';

const timeInput = cn(controlClasses, 'py-1.5 text-sm');

const allRestaurants = [
  'Inova Foz - Vila do Conde',
  'Inova Regio - Vila do Conde',
  'Inova Trindade - Porto',
  'Inova Beach - Azurara',
];

const RESTAURANT_SLUG_MAP = {
  'Inova Foz - Vila do Conde': 'foz',
  'Inova Regio - Vila do Conde': 'regio',
  'Inova Trindade - Porto': 'trindade',
  'Inova Beach - Azurara': 'beach',
};

const SLUG_TO_NAME_MAP = {
  'foz': 'Inova Foz - Vila do Conde',
  'regio': 'Inova Regio - Vila do Conde',
  'trindade': 'Inova Trindade - Porto',
  'beach': 'Inova Beach - Azurara',
};

const daysOfWeek = [
  { value: 'monday', label: 'Segunda-feira' },
  { value: 'tuesday', label: 'Terça-feira' },
  { value: 'wednesday', label: 'Quarta-feira' },
  { value: 'thursday', label: 'Quinta-feira' },
  { value: 'friday', label: 'Sexta-feira' },
  { value: 'saturday', label: 'Sábado' },
  { value: 'sunday', label: 'Domingo' },
];

// Máximo de dias que o fecho em intervalo aceita de uma só vez.
const MAX_CLOSED_RANGE_DAYS = 90;

// Placeholder mostrado enquanto a configuração real não chega do backend.
// Nunca é gravado: o botão Salvar fica bloqueado até a carga ter sucesso.
const createDefaultShifts = () =>
  daysOfWeek.reduce((acc, day) => {
    acc[day.value] = [{ from: '12:00', to: '14:00' }, { from: '19:00', to: '21:00' }];
    return acc;
  }, {});

const createDefaultRestaurantConfig = () => ({
  weeklyShifts: createDefaultShifts(),
  holidays: [],
  maxCapacity: 50,
  reservationInterval: 30,
  maxReservationPercentage: 80,
});

// Cada restaurante recebe o seu próprio objeto — nunca partilhar referências
// entre separadores, senão editar um deles altera os outros.
const createInitialConfig = () =>
  allRestaurants.reduce((acc, restaurant) => {
    acc[restaurant] = createDefaultRestaurantConfig();
    return acc;
  }, {});

// Helper: Convert DD/MM/YYYY to YYYY-MM-DD (backend -> frontend)
const convertDateToInput = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('/');
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}-${parts[1]}-${parts[0]}`; // YYYY-MM-DD
};

// Helper: Convert YYYY-MM-DD to DD/MM/YYYY (frontend -> backend)
const convertDateToBackend = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}/${parts[1]}/${parts[0]}`; // DD/MM/YYYY
};

// Expande um intervalo YYYY-MM-DD em datas individuais. Usa UTC para que a
// aritmética de dias não seja afetada pela mudança da hora legal.
const eachDateInRange = (fromIso, toIso) => {
  const dates = [];
  const start = Date.parse(`${fromIso}T00:00:00Z`);
  const end = Date.parse(`${toIso}T00:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return dates;
  const DAY_MS = 24 * 60 * 60 * 1000;
  for (let ms = start; ms <= end; ms += DAY_MS) {
    dates.push(new Date(ms).toISOString().slice(0, 10));
  }
  return dates;
};

const AdminConfig = () => {
  const token = localStorage.getItem('toms_auth_token');
  const [activeTab, setActiveTab] = useState(allRestaurants[0]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  // Restaurantes cuja configuração chegou mesmo do backend. Só estes podem ser
  // gravados — caso contrário estaríamos a escrever os placeholders por cima do
  // horário real (foi assim que os domingos de Azurara ficaram fechados).
  const [loadedRestaurants, setLoadedRestaurants] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [closureSummary, setClosureSummary] = useState({ weekdays: [], dates: [] });
  const [closeDaysOpen, setCloseDaysOpen] = useState(false);
  const [closeDaysForm, setCloseDaysForm] = useState({ from: '', to: '', reason: '' });
  const [closeDaysError, setCloseDaysError] = useState(null);
  const [closeDaysConflicts, setCloseDaysConflicts] = useState([]);

  const [config, setConfig] = useState(createInitialConfig);

  const currentConfig = config[activeTab];
  const isLoaded = Boolean(loadedRestaurants[activeTab]);

  // Fetch configurations on mount
  useEffect(() => {
    const fetchConfigs = async () => {
      if (!token) {
        setError('Token de autenticação não encontrado');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        logger.info('Fetching restaurant configurations');

        const response = await getRestaurantConfigs(API_BASE_URL, token);

        if (response.success && response.data) {
          logger.info('Restaurant configurations loaded', { count: Object.keys(response.data).length });

          // Partir dos placeholders e substituir apenas o que o backend devolveu,
          // para que um restaurante em falta não desapareça do estado.
          const mappedConfig = createInitialConfig();
          const loaded = {};

          Object.keys(response.data).forEach(slug => {
            const restaurantName = SLUG_TO_NAME_MAP[slug];
            if (!restaurantName) return;
            const backendConfig = response.data[slug];

            // Convert backend format to frontend format
            mappedConfig[restaurantName] = {
              weeklyShifts: daysOfWeek.reduce((acc, day) => {
                acc[day.value] = backendConfig.weekly_shifts?.[day.value]?.map(s => ({ from: s.start, to: s.end })) || [];
                return acc;
              }, {}),
              holidays: backendConfig.holidays?.map(h => ({
                date: convertDateToInput(h.date), // Convert DD/MM/YYYY -> YYYY-MM-DD
                name: h.name || '',
                shifts: h.shifts?.map(s => ({ from: s.start, to: s.end })) || [],
                // Sem turnos = dia fechado. É esta a convenção que o backend usa.
                closed: !(h.shifts?.length),
              })) || [],
              maxCapacity: backendConfig.general_settings?.max_capacity || 50,
              reservationInterval: backendConfig.general_settings?.reservation_interval || 30,
              maxReservationPercentage: backendConfig.general_settings?.max_reservation_percentage || 80,
            };
            loaded[restaurantName] = true;
          });

          setConfig(mappedConfig);
          setLoadedRestaurants(loaded);

          const missing = allRestaurants.filter(r => !loaded[r]);
          if (missing.length > 0) {
            logger.error('Restaurant configurations missing from response', { missing });
            setError(`O servidor não devolveu a configuração de: ${missing.join(', ')}. Esses separadores ficam bloqueados para gravação.`);
          }
        } else {
          throw new Error('Formato de resposta inválido');
        }
      } catch (err) {
        logger.error('Failed to fetch restaurant configurations', { error: err.message });
        setError(err.message || 'Erro ao carregar configurações');
      } finally {
        setLoading(false);
      }
    };

    fetchConfigs();
  }, [token]);

  // Todas as edições passam por aqui: cópia imutável do restaurante ativo.
  const updateActiveRestaurant = (updater) => {
    setNotice(null);
    setValidationErrors([]);
    setConfig(prev => ({
      ...prev,
      [activeTab]: updater(prev[activeTab]),
    }));
  };

  const updateWeeklyDay = (day, updateShifts) =>
    updateActiveRestaurant(restaurant => ({
      ...restaurant,
      weeklyShifts: {
        ...restaurant.weeklyShifts,
        [day]: updateShifts(restaurant.weeklyShifts[day]),
      },
    }));

  const updateHoliday = (index, updateEntry) =>
    updateActiveRestaurant(restaurant => ({
      ...restaurant,
      holidays: restaurant.holidays.map((holiday, i) => (i === index ? updateEntry(holiday) : holiday)),
    }));

  const handleAddShift = (day) =>
    updateWeeklyDay(day, shifts => [...shifts, { from: '12:00', to: '14:00' }]);

  const handleRemoveShift = (day, index) =>
    updateWeeklyDay(day, shifts => shifts.filter((_, i) => i !== index));

  const handleShiftChange = (day, index, field, value) =>
    updateWeeklyDay(day, shifts => shifts.map((s, i) => (i === index ? { ...s, [field]: value } : s)));

  const handleAddHoliday = () =>
    updateActiveRestaurant(restaurant => ({
      ...restaurant,
      holidays: [...restaurant.holidays, { date: '', name: '', shifts: [{ from: '12:00', to: '14:00' }], closed: false }],
    }));

  const handleRemoveHoliday = (index) =>
    updateActiveRestaurant(restaurant => ({
      ...restaurant,
      holidays: restaurant.holidays.filter((_, i) => i !== index),
    }));

  const handleHolidayChange = (index, field, value) =>
    updateHoliday(index, holiday => ({ ...holiday, [field]: value }));

  const handleAddHolidayShift = (holidayIndex) =>
    updateHoliday(holidayIndex, holiday => ({
      ...holiday,
      shifts: [...holiday.shifts, { from: '12:00', to: '14:00' }],
    }));

  const handleRemoveHolidayShift = (holidayIndex, shiftIndex) =>
    updateHoliday(holidayIndex, holiday => ({
      ...holiday,
      shifts: holiday.shifts.filter((_, i) => i !== shiftIndex),
    }));

  const handleHolidayShiftChange = (holidayIndex, shiftIndex, field, value) =>
    updateHoliday(holidayIndex, holiday => ({
      ...holiday,
      shifts: holiday.shifts.map((s, i) => (i === shiftIndex ? { ...s, [field]: value } : s)),
    }));

  // Converte um dia fechado de volta a dia com horário especial.
  const handleReopenHoliday = (holidayIndex) =>
    updateHoliday(holidayIndex, holiday => ({
      ...holiday,
      closed: false,
      shifts: holiday.shifts.length > 0 ? holiday.shifts : [{ from: '12:00', to: '14:00' }],
    }));

  const handleConfigChange = (field, value) =>
    updateActiveRestaurant(restaurant => ({ ...restaurant, [field]: value }));

  // --- Fechar dias (única forma suportada de fechar o restaurante numa data) ---

  const openCloseDaysModal = () => {
    setCloseDaysForm({ from: '', to: '', reason: '' });
    setCloseDaysError(null);
    setCloseDaysConflicts([]);
    setCloseDaysOpen(true);
  };

  const handleConfirmCloseDays = () => {
    const from = closeDaysForm.from;
    const to = closeDaysForm.to || closeDaysForm.from;

    if (!from) {
      setCloseDaysError('Escolhe pelo menos a data inicial.');
      return;
    }
    if (to < from) {
      setCloseDaysError('A data final não pode ser anterior à data inicial.');
      return;
    }

    const dates = eachDateInRange(from, to);
    if (dates.length === 0) {
      setCloseDaysError('Intervalo de datas inválido.');
      return;
    }
    if (dates.length > MAX_CLOSED_RANGE_DAYS) {
      setCloseDaysError(`O intervalo tem ${dates.length} dias. O máximo de cada vez é ${MAX_CLOSED_RANGE_DAYS} dias.`);
      return;
    }

    // Datas que já têm horário especial configurado: fechar substitui esse horário.
    const conflicts = currentConfig.holidays
      .filter(h => !h.closed && h.shifts.length > 0 && dates.includes(h.date))
      .map(h => h.date);

    if (conflicts.length > 0 && closeDaysConflicts.length === 0) {
      setCloseDaysConflicts(conflicts);
      setCloseDaysError(null);
      return;
    }

    const dateSet = new Set(dates);
    updateActiveRestaurant(restaurant => ({
      ...restaurant,
      holidays: [
        ...restaurant.holidays.filter(h => !dateSet.has(h.date)),
        ...dates.map(date => ({
          date,
          name: closeDaysForm.reason.trim(),
          shifts: [],
          closed: true,
        })),
      ].sort((a, b) => a.date.localeCompare(b.date)),
    }));

    setCloseDaysOpen(false);
    setCloseDaysConflicts([]);
    setNotice({
      type: 'info',
      text: `${dates.length === 1 ? '1 dia marcado' : `${dates.length} dias marcados`} como fechado. Clica em "Salvar Configurações" para aplicar.`,
    });
  };

  // --- Validação ---

  const validateRestaurantConfig = (restaurant) => {
    const errors = [];

    daysOfWeek.forEach(day => {
      restaurant.weeklyShifts[day.value].forEach((shift, index) => {
        if (!shift.from || !shift.to || shift.to <= shift.from) {
          errors.push(`${day.label}: o turno ${index + 1} (${shift.from || '--:--'} → ${shift.to || '--:--'}) é inválido. A hora de fim tem de ser depois da hora de início.`);
        }
      });
    });

    const seenDates = new Set();
    restaurant.holidays.forEach((holiday, index) => {
      const label = holiday.date ? convertDateToBackend(holiday.date) : `dia especial #${index + 1}`;

      if (!holiday.date) {
        errors.push(`O dia especial #${index + 1} não tem data preenchida. Preenche a data ou remove-o.`);
      } else if (seenDates.has(holiday.date)) {
        errors.push(`Há mais do que um dia especial para ${label}. Mantém apenas um.`);
      } else {
        seenDates.add(holiday.date);
      }

      if (!holiday.closed && holiday.shifts.length === 0) {
        errors.push(`O dia especial ${label} não tem nenhum turno. Um dia sem turnos fica fechado para reservas — adiciona pelo menos um turno ou usa "Marcar dias como fechados" se o objetivo é mesmo fechar.`);
      }

      if (!holiday.closed) {
        holiday.shifts.forEach((shift, shiftIndex) => {
          if (!shift.from || !shift.to || shift.to <= shift.from) {
            errors.push(`${label}: o turno ${shiftIndex + 1} (${shift.from || '--:--'} → ${shift.to || '--:--'}) é inválido. A hora de fim tem de ser depois da hora de início.`);
          }
        });
      }
    });

    return errors;
  };

  const buildClosureSummary = (restaurant) => ({
    weekdays: daysOfWeek.filter(day => restaurant.weeklyShifts[day.value].length === 0).map(day => day.label),
    dates: restaurant.holidays
      .filter(h => h.closed && h.date)
      .map(h => ({ date: convertDateToBackend(h.date), name: h.name })),
  });

  const handleSave = () => {
    setNotice(null);
    setError(null);

    if (!token) {
      setError('Token de autenticação não encontrado. Por favor, faça login novamente.');
      return;
    }
    if (!isLoaded) {
      setError('A configuração deste restaurante não foi carregada do servidor. Recarrega a página antes de gravar — caso contrário gravarias valores por omissão por cima do horário real.');
      return;
    }

    const errors = validateRestaurantConfig(currentConfig);
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors([]);

    const summary = buildClosureSummary(currentConfig);
    if (summary.weekdays.length > 0 || summary.dates.length > 0) {
      setClosureSummary(summary);
      setConfirmOpen(true);
      return;
    }

    persistConfig();
  };

  const persistConfig = async () => {
    try {
      setSaving(true);
      setError(null);

      const slug = RESTAURANT_SLUG_MAP[activeTab];
      const currentRestaurantConfig = config[activeTab];

      logger.info('Saving restaurant configuration', { restaurant: activeTab, slug });

      // Convert frontend format to backend format
      const configData = {
        general_settings: {
          max_capacity: currentRestaurantConfig.maxCapacity,
          reservation_interval: currentRestaurantConfig.reservationInterval,
          max_reservation_percentage: currentRestaurantConfig.maxReservationPercentage,
        },
        weekly_shifts: daysOfWeek.reduce((acc, day) => {
          acc[day.value] = currentRestaurantConfig.weeklyShifts[day.value].map(s => ({ start: s.from, end: s.to }));
          return acc;
        }, {}),
        // Dia fechado = dia especial com lista de turnos vazia (convenção do backend).
        holidays: currentRestaurantConfig.holidays.map(h => ({
          date: convertDateToBackend(h.date), // Convert YYYY-MM-DD -> DD/MM/YYYY
          name: h.name,
          shifts: h.closed ? [] : h.shifts.map(s => ({ start: s.from, end: s.to })),
        })),
      };

      const response = await updateRestaurantConfig(API_BASE_URL, token, slug, configData);

      if (response.success) {
        logger.info('Configuration saved successfully', { restaurant: activeTab });
        setConfirmOpen(false);
        setNotice({ type: 'success', text: `Configuração de ${activeTab} salva com sucesso.` });
      } else {
        throw new Error(response.message || 'Erro ao salvar configuração');
      }
    } catch (err) {
      logger.error('Failed to save configuration', { restaurant: activeTab, error: err.message });
      setConfirmOpen(false);
      setError(err.message || 'Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-brand animate-spin mx-auto mb-4" />
          <p className="text-muted text-lg">Carregando configurações...</p>
        </div>
      </div>
    );
  }

  const tabs = (
    <div className="-mx-4 sm:-mx-6 px-4 sm:px-6 border-t border-border pt-1">
      <div className="flex gap-1 flex-wrap">
        {allRestaurants.map((restaurant) => (
          <button
            key={restaurant}
            onClick={() => {
              setActiveTab(restaurant);
              setNotice(null);
              setValidationErrors([]);
            }}
            className={cn(
              'px-4 py-2.5 text-sm font-semibold rounded-t-lg transition-colors border-b-2',
              activeTab === restaurant
                ? 'border-brand text-brand bg-brand-soft'
                : 'border-transparent text-muted hover:text-foreground hover:bg-surface-2'
            )}
          >
            {restaurant}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <PageShell
      title="Configurações de Administração"
      icon={Settings}
      headerExtra={tabs}
      actions={
        <Button
          variant="primary"
          icon={saving ? undefined : Save}
          loading={saving}
          disabled={!isLoaded}
          onClick={handleSave}
        >
          {saving ? 'Salvando...' : 'Salvar Configurações'}
        </Button>
      }
    >
      <div className="space-y-8">
        {!isLoaded && (
          <div className="bg-status-canceled-soft border border-status-canceled/30 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-status-canceled flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-status-canceled font-semibold">
                Configuração não carregada
              </p>
              <p className="text-sm text-status-canceled/90 mt-1">
                Não foi possível carregar a configuração de {activeTab} a partir do servidor. Os valores
                abaixo são apenas de exemplo e a gravação está bloqueada, para não substituir os horários
                reais. Recarrega a página para tentar de novo.
              </p>
            </div>
          </div>
        )}

        {notice && (
          <div
            className={cn(
              'rounded-xl p-4 flex items-start gap-3 border',
              notice.type === 'success'
                ? 'bg-status-confirmed-soft border-status-confirmed/30'
                : 'bg-brand-soft border-brand/30'
            )}
          >
            {notice.type === 'success' ? (
              <CheckCircle2 className="h-5 w-5 text-status-confirmed flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="h-5 w-5 text-brand flex-shrink-0 mt-0.5" />
            )}
            <p className="text-sm text-foreground">{notice.text}</p>
          </div>
        )}

        {validationErrors.length > 0 && (
          <div className="bg-status-canceled-soft border border-status-canceled/30 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-status-canceled flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-status-canceled font-semibold">
                Não foi possível salvar — corrige o seguinte:
              </p>
              <ul className="mt-2 space-y-1 list-disc list-inside">
                {validationErrors.map((message, index) => (
                  <li key={index} className="text-sm text-status-canceled/90">{message}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* General Settings */}
        <Card className="p-6">
          <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
            <Settings className="h-5 w-5 text-brand" />
            Configurações Gerais
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                <Users className="inline h-4 w-4 mr-1" />
                Capacidade Máxima do Restaurante
              </label>
              <input
                type="number"
                min="1"
                value={currentConfig.maxCapacity}
                onChange={(e) => handleConfigChange('maxCapacity', parseInt(e.target.value))}
                className={cn(controlClasses, 'w-full')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                <Clock className="inline h-4 w-4 mr-1" />
                Intervalo de Reserva (minutos)
              </label>
              <select
                value={currentConfig.reservationInterval}
                onChange={(e) => handleConfigChange('reservationInterval', parseInt(e.target.value))}
                className={cn(controlClasses, 'w-full')}
              >
                <option value={15}>15 minutos</option>
                <option value={30}>30 minutos</option>
                <option value={60}>60 minutos</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                <Percent className="inline h-4 w-4 mr-1" />
                % Máxima de Reservas Aceitas
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={currentConfig.maxReservationPercentage}
                onChange={(e) => handleConfigChange('maxReservationPercentage', parseInt(e.target.value))}
                className={cn(controlClasses, 'w-full')}
              />
              <p className="text-xs text-muted-2 mt-1">
                Aplicado a todos os turnos deste restaurante
              </p>
            </div>
          </div>
        </Card>

        {/* Weekly Shifts */}
        <Card className="p-6">
          <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-brand" />
            Horários Semanais de Reserva
          </h2>
          <div className="space-y-6">
            {daysOfWeek.map((day) => (
              <div key={day.value} className="border-b border-border pb-4 last:border-b-0">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-foreground">{day.label}</h3>
                  <Button variant="outline" size="sm" icon={Plus} onClick={() => handleAddShift(day.value)}>
                    Adicionar Turno
                  </Button>
                </div>
                <div className="space-y-2">
                  {currentConfig.weeklyShifts[day.value].length === 0 ? (
                    <p className="text-sm font-medium text-status-canceled flex items-center gap-2">
                      <CalendarOff className="h-4 w-4" />
                      Sem turnos — fechado para reservas todas as semanas
                    </p>
                  ) : (
                    currentConfig.weeklyShifts[day.value].map((shift, index) => (
                      <div key={index} className="flex items-center gap-3 bg-surface-2 p-3 rounded-xl">
                        <div className="flex items-center gap-2 flex-1 flex-wrap">
                          <label className="text-sm font-medium text-muted">De:</label>
                          <input
                            type="time"
                            value={shift.from}
                            onChange={(e) => handleShiftChange(day.value, index, 'from', e.target.value)}
                            className={timeInput}
                          />
                          <label className="text-sm font-medium text-muted">Até:</label>
                          <input
                            type="time"
                            value={shift.to}
                            onChange={(e) => handleShiftChange(day.value, index, 'to', e.target.value)}
                            className={timeInput}
                          />
                        </div>
                        <button
                          onClick={() => handleRemoveShift(day.value, index)}
                          className="p-2 text-status-canceled hover:bg-status-canceled-soft rounded-lg transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Holidays / Special Days */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
              <Calendar className="h-5 w-5 text-brand" />
              Feriados / Dias Especiais
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="secondary" icon={CalendarOff} onClick={openCloseDaysModal}>
                Marcar dias como fechados
              </Button>
              <Button icon={Plus} onClick={handleAddHoliday}>
                Adicionar Dia Especial
              </Button>
            </div>
          </div>
          <p className="text-sm text-muted mb-4">
            Um dia especial substitui por completo o horário semanal dessa data. Para fechar o restaurante
            num dia usa <strong>"Marcar dias como fechados"</strong> — é a única forma suportada de o fazer.
          </p>
          <div className="space-y-4">
            {currentConfig.holidays.length === 0 ? (
              <p className="text-muted-2 text-center py-8">
                Nenhum dia especial configurado. Clique em "Adicionar Dia Especial" para começar.
              </p>
            ) : (
              currentConfig.holidays.map((holiday, holidayIndex) => (
                <div
                  key={holidayIndex}
                  className={cn(
                    'border rounded-xl p-4',
                    holiday.closed
                      ? 'border-status-canceled/30 bg-status-canceled-soft'
                      : 'border-border bg-surface-2'
                  )}
                >
                  <div className="flex items-start gap-4 mb-3">
                    <div className="flex-1">
                      <div className="mb-3">
                        {holiday.closed ? (
                          <Badge className="bg-status-canceled text-white gap-1">
                            <CalendarOff className="h-3 w-3" />
                            Fechado
                          </Badge>
                        ) : (
                          <Badge className="bg-brand-soft text-brand gap-1">
                            <Clock className="h-3 w-3" />
                            Horário especial
                          </Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">Data</label>
                          <input
                            type="date"
                            value={holiday.date}
                            onChange={(e) => handleHolidayChange(holidayIndex, 'date', e.target.value)}
                            className={cn(controlClasses, 'w-full')}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">
                            {holiday.closed ? 'Motivo (opcional)' : 'Nome (opcional)'}
                          </label>
                          <input
                            type="text"
                            value={holiday.name}
                            onChange={(e) => handleHolidayChange(holidayIndex, 'name', e.target.value)}
                            placeholder={holiday.closed ? 'Ex: Férias, Obras' : 'Ex: Natal, Ano Novo'}
                            className={cn(controlClasses, 'w-full')}
                          />
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveHoliday(holidayIndex)}
                      className="p-2 text-status-canceled hover:bg-status-canceled-soft rounded-lg transition-colors"
                      aria-label="Remover dia especial"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  {holiday.closed ? (
                    <div className="sm:ml-4 flex items-center justify-between gap-3 flex-wrap">
                      <p className="text-sm text-status-canceled">
                        O restaurante não aceita reservas nesta data.
                      </p>
                      <Button variant="outline" size="sm" icon={Clock} onClick={() => handleReopenHoliday(holidayIndex)}>
                        Reabrir com horário
                      </Button>
                    </div>
                  ) : (
                    <div className="sm:ml-4 space-y-2">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-semibold text-foreground">Turnos para este dia:</label>
                        <Button variant="outline" size="sm" icon={Plus} onClick={() => handleAddHolidayShift(holidayIndex)}>
                          Adicionar Turno
                        </Button>
                      </div>
                      {holiday.shifts.length === 0 && (
                        <p className="text-sm font-medium text-status-canceled">
                          Sem turnos. Um dia especial sem turnos fecha o restaurante — adiciona um turno ou
                          usa "Marcar dias como fechados".
                        </p>
                      )}
                      {holiday.shifts.map((shift, shiftIndex) => (
                        <div key={shiftIndex} className="flex items-center gap-3 bg-surface p-2 rounded-xl border border-border">
                          <div className="flex items-center gap-2 flex-1 flex-wrap">
                            <label className="text-xs font-medium text-muted">De:</label>
                            <input
                              type="time"
                              value={shift.from}
                              onChange={(e) => handleHolidayShiftChange(holidayIndex, shiftIndex, 'from', e.target.value)}
                              className={timeInput}
                            />
                            <label className="text-xs font-medium text-muted">Até:</label>
                            <input
                              type="time"
                              value={shift.to}
                              onChange={(e) => handleHolidayShiftChange(holidayIndex, shiftIndex, 'to', e.target.value)}
                              className={timeInput}
                            />
                          </div>
                          <button
                            onClick={() => handleRemoveHolidayShift(holidayIndex, shiftIndex)}
                            disabled={holiday.shifts.length === 1}
                            title={
                              holiday.shifts.length === 1
                                ? 'Este é o único turno do dia. Para fechar o dia usa "Marcar dias como fechados".'
                                : 'Remover turno'
                            }
                            className="p-1 text-status-canceled hover:bg-status-canceled-soft rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Error Display */}
        {error && (
          <div className="bg-status-canceled-soft border border-status-canceled/30 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-status-canceled flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-status-canceled font-semibold">Erro ao carregar/salvar configurações</p>
              <p className="text-sm text-status-canceled/90 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="bg-status-confirmed-soft border border-status-confirmed/30 rounded-xl p-4">
          <p className="text-sm text-foreground">
            <strong>Nota:</strong> As configurações são salvas na base de dados WordPress (tabela wp_options).
            Após salvar, essas configurações estarão disponíveis para o formulário público de reservas no site.
          </p>
        </div>
      </div>

      {/* Modal: marcar dias como fechados */}
      <Modal
        open={closeDaysOpen}
        onClose={() => setCloseDaysOpen(false)}
        title="Marcar dias como fechados"
        subtitle={`${activeTab} — o formulário público deixa de aceitar reservas nestas datas`}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCloseDaysOpen(false)}>Cancelar</Button>
            <Button variant="danger" icon={CalendarOff} onClick={handleConfirmCloseDays}>
              {closeDaysConflicts.length > 0 ? 'Substituir e fechar' : 'Fechar dias'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">De</label>
              <input
                type="date"
                value={closeDaysForm.from}
                onChange={(e) => {
                  setCloseDaysForm(prev => ({ ...prev, from: e.target.value }));
                  setCloseDaysConflicts([]);
                }}
                className={cn(controlClasses, 'w-full')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Até (opcional)</label>
              <input
                type="date"
                value={closeDaysForm.to}
                onChange={(e) => {
                  setCloseDaysForm(prev => ({ ...prev, to: e.target.value }));
                  setCloseDaysConflicts([]);
                }}
                className={cn(controlClasses, 'w-full')}
              />
              <p className="text-xs text-muted-2 mt-1">Deixa vazio para fechar apenas um dia.</p>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Motivo (opcional)</label>
            <input
              type="text"
              value={closeDaysForm.reason}
              onChange={(e) => setCloseDaysForm(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="Ex: Férias, Obras, Feriado"
              className={cn(controlClasses, 'w-full')}
            />
          </div>

          {closeDaysConflicts.length > 0 && (
            <div className="bg-status-pending-soft border border-status-pending/30 rounded-xl p-3">
              <p className="text-sm text-foreground">
                Estas datas já têm um horário especial configurado e vão ser substituídas por "fechado":{' '}
                <strong>{closeDaysConflicts.map(convertDateToBackend).join(', ')}</strong>
              </p>
            </div>
          )}

          {closeDaysError && (
            <div className="bg-status-canceled-soft border border-status-canceled/30 rounded-xl p-3">
              <p className="text-sm text-status-canceled">{closeDaysError}</p>
            </div>
          )}
        </div>
      </Modal>

      {/* Modal: confirmação antes de gravar fechos */}
      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Confirmar dias fechados"
        subtitle={`${activeTab} — confirma antes de gravar`}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={saving}>Cancelar</Button>
            <Button variant="primary" icon={saving ? undefined : Save} loading={saving} onClick={persistConfig}>
              Confirmar e salvar
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-foreground">
            Com esta configuração o restaurante <strong>não aceita reservas</strong> em:
          </p>
          {closureSummary.weekdays.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-foreground mb-1">Todas as semanas</p>
              <ul className="list-disc list-inside space-y-0.5">
                {closureSummary.weekdays.map(label => (
                  <li key={label} className="text-sm text-status-canceled">{label}</li>
                ))}
              </ul>
            </div>
          )}
          {closureSummary.dates.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-foreground mb-1">Datas específicas</p>
              <ul className="list-disc list-inside space-y-0.5">
                {closureSummary.dates.map(({ date, name }) => (
                  <li key={date} className="text-sm text-status-canceled">
                    {date}{name ? ` — ${name}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Modal>
    </PageShell>
  );
};

export default AdminConfig;
